import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../../config';
import {
  discardDraft,
  finalizeCard,
  generateSuggestions,
  loadWorkflowContext,
  newDraftRow,
  upsertDraft,
} from '../../app/coach';
import type { CoachFailureCode, WorkflowEntry } from '../../app/coach';
import { newId } from '../../app/ids';
import { log } from '../../app/logger';
import { loadRoster } from '../../app/roster';
import { EMPTY_TEAM_SHAPE, loadTeamShape } from '../../app/settingsB';
import type { AdjustmentEntry, CoachCardContext, CoachCardRow, HypothesisPhase, MatchRow, OurPlayerRow, TeamShape } from '../../types';
import {
  aiEntryB,
  buildCoachRequestB,
  CARD_FULL_NOTE,
  editEntryB,
  manualEntryB,
  removeEntryB,
  replaceEntryB,
  setEntryStatusB,
  toggleSelectionB,
  truncationNoticeB,
} from './workflowB';

/**
 * B-15 — the **adjustments workflow's controller** (design spec §2.5; scenarios 22–28). State,
 * the interaction-grain commits, and the one network call. No rendering.
 *
 * ── Persistence first: the draft row IS the workflow's state ────────────────
 *
 * §2.5 opens with *"db-driven, no zombie states"*: opening the workflow creates (or reopens) the
 * draft for (match, context), and **every structural act commits immediately** — select toggle,
 * accept, reject, save-edit, add, remove. There is no debounce anywhere and no in-memory session
 * to lose: a kill at any point costs at most an open editor's unsaved text, and a relaunch
 * re-renders the workflow from rows (which step to reopen on is derived from the row, below).
 *
 * The consequence is deliberate and is the design's: a peek at the workflow leaves a draft, and
 * the draft is resolved by finalizing it or by the one-tap Discard (which is also F-1's
 * completion guard's resolution, and why B-18's Needs-finishing queue lists draft holders).
 *
 * ── Commits serialize; the screen renders what committed ───────────────────
 *
 * Each act is queued on one promise chain and **builds from the last committed row**, so two
 * taps 80 ms apart cannot both fork from the same state and lose one. Nothing renders
 * optimistically: the facade's convention is a dispatch and an imperative re-read, so a ticked
 * checkbox means a committed row — which is exactly what the browser pass probes at.
 *
 * ── The generate is bounded, cancellable, and writes nothing until it lands ─
 *
 * One `AbortController` per attempt: the `COACH_TIMEOUT_MS` bound lives in the facade, and this
 * hook aborts on any `Match.state` change and on unmount (§2.5's boundary behavior — a response
 * arriving after the second-half whistle must commit nothing and surface nothing). A cancelled
 * attempt is not a failure and renders no copy.
 */

export type WorkflowStepB = 'select' | 'generate' | 'review' | 'done';
export type WorkflowPhaseB = 'loading' | 'ready' | 'failed';

/** What the Add/Edit sheet hands back — the entry's three editable facts. */
export interface AdjustmentDraftB {
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly sourceHypothesisIds: readonly string[];
}

const MODULE = 'ui-b/workflow';

export function useWorkflowB(match: MatchRow, context: CoachCardContext) {
  const [phase, setPhase] = useState<WorkflowPhaseB>('loading');
  const [step, setStep] = useState<WorkflowStepB>('select');
  const [entries, setEntries] = useState<readonly WorkflowEntry[]>([]);
  const [roster, setRoster] = useState<readonly OurPlayerRow[]>([]);
  /**
   * §15.4 — read through `loadTeamShape`, **never through `loadSettings()`**: a bare settings read
   * would pull `backupCredential` into the same memory as the payload builder.
   */
  const [shape, setShape] = useState<TeamShape>(EMPTY_TEAM_SHAPE);
  const [card, setCard] = useState<CoachCardRow | null>(null);
  /**
   * B-16 — the halftime card, whatever this flow's context: §2.5 step 4 shows it as reference
   * context at the top of the FULL-TIME flow (`referenceCardB` decides when). It is the same row
   * as `card` at a halftime flow, which is why the decision is a function and not a `&&` here.
   */
  const [halftimeCard, setHalftimeCard] = useState<CoachCardRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<CoachFailureCode | null>(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [busy, setBusy] = useState(false);

  /** The last COMMITTED row. Every queued act builds from this, never from rendered state. */
  const latest = useRef<CoachCardRow | null>(null);
  /** One write at a time, in press order (the `useSetup` serialized-chain precedent). */
  const chain = useRef<Promise<void>>(Promise.resolve());
  const generating = useRef<AbortController | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ── Open (or reopen) the draft — §2.5's first sentence ─────────────────────

  const open = useCallback(async (): Promise<void> => {
    const [ctx, players, teamShape] = await Promise.all([
      loadWorkflowContext(match.id, context),
      loadRoster(),
      loadTeamShape(),
    ]);
    if (!alive.current) return;
    setEntries(ctx.entries);
    setRoster(players);
    setShape(teamShape);
    setHalftimeCard(ctx.halftimeCard ?? null);

    const existing = ctx.card;
    if (existing !== undefined && existing.status === 'final') {
      // A finalized card is immutable — there is no draft to reopen and nothing to commit.
      latest.current = existing;
      setCard(existing);
      setStep('done');
      setPhase('ready');
      return;
    }

    const row = existing ?? newDraftRow(match.id, context);
    if (existing === undefined) {
      const result = await upsertDraft(row);
      if (!alive.current) return;
      if (!result.ok) {
        // The engine refused the card outright (a closed window, or scenario 20's
        // advanceScout+halftime). Say so; do not render a workflow that can never commit.
        setNotice(result.message);
        setPhase('failed');
        return;
      }
    }
    latest.current = row;
    setCard(row);
    // §2.5 — relaunch re-renders from rows: a draft that already holds entries reopens on the
    // review step, because that is where its user was.
    setStep(row.adjustments.length > 0 ? 'review' : 'select');
    setPhase('ready');
  }, [match.id, context]);

  useEffect(() => {
    open().catch((cause: unknown) => {
      const message = cause instanceof Error ? cause.message : String(cause);
      setNotice(message);
      setPhase('failed');
      log.error(MODULE, 'the adjustments workflow could not be opened', { message });
    });
  }, [open]);

  /** Re-read the committed row after a rejection — the surface must never show a lost write. */
  const reread = useCallback(async (): Promise<void> => {
    const ctx = await loadWorkflowContext(match.id, context);
    if (!alive.current) return;
    setEntries(ctx.entries);
    setHalftimeCard(ctx.halftimeCard ?? null);
    latest.current = ctx.card ?? null;
    setCard(ctx.card ?? null);
  }, [match.id, context]);

  // ── Interaction-grain commits ──────────────────────────────────────────────

  /**
   * Queue one structural act. `build` receives the last committed row and returns the whole next
   * row (or `null` to decline — a stale tap against a row that moved on). Nothing renders until
   * the dispatch resolves ok.
   */
  const enqueue = useCallback(
    (
      what: string,
      build: (current: CoachCardRow) => CoachCardRow | null,
      after?: (committed: CoachCardRow) => void,
    ): void => {
      setBusy(true);
      chain.current = chain.current
        .then(async () => {
          const current = latest.current;
          if (current === null || current.status === 'final') return;
          const next = build(current);
          if (next === null) return;
          const result = await upsertDraft(next);
          if (!alive.current) return;
          if (!result.ok) {
            setNotice(result.message);
            await reread();
            return;
          }
          latest.current = next;
          setCard(next);
          setNotice(null);
          after?.(next);
        })
        .catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, `${what} could not be committed`, { message });
        })
        .finally(() => {
          if (alive.current) setBusy(false);
        });
    },
    [reread],
  );

  const orderedIds = entries.map((entry) => entry.hypothesis.id);

  const toggleSelection = useCallback(
    (hypothesisId: string): void => {
      enqueue('a selection toggle', (current) => ({
        ...current,
        selectedHypothesisIds: toggleSelectionB(
          current.selectedHypothesisIds,
          hypothesisId,
          orderedIds,
        ),
      }));
    },
    [enqueue, orderedIds],
  );

  /** Scenario 23 — all, or none. No cap exists on either side of this control. */
  const selectAll = useCallback(
    (on: boolean): void => {
      enqueue('a select-all', (current) => ({
        ...current,
        selectedHypothesisIds: on ? [...orderedIds] : [],
      }));
    },
    [enqueue, orderedIds],
  );

  const setEntryStatus = useCallback(
    (entry: AdjustmentEntry, status: AdjustmentEntry['status']): void => {
      enqueue('an accept/reject', (current) =>
        replaceEntryB(current, setEntryStatusB(entry, status)),
      );
    },
    [enqueue],
  );

  const saveEdit = useCallback(
    (entry: AdjustmentEntry, draft: AdjustmentDraftB): void => {
      enqueue('an edit', (current) =>
        replaceEntryB(
          current,
          editEntryB(entry, draft.text, draft.phase, draft.sourceHypothesisIds),
        ),
      );
    },
    [enqueue],
  );

  const addManual = useCallback(
    (draft: AdjustmentDraftB): void => {
      enqueue('a manual adjustment', (current) => {
        if (current.adjustments.length >= config.COACH_CARD_MAX_ENTRIES) return null;
        return {
          ...current,
          adjustments: [...current.adjustments, manualEntryB(draft, newId(), Date.now())],
        };
      });
    },
    [enqueue],
  );

  const removeEntry = useCallback(
    (entryId: string): void => {
      enqueue('a remove', (current) => removeEntryB(current, entryId));
    },
    [enqueue],
  );

  // ── Step 2 — the generate (§5.2/§5.4/§5.5) ─────────────────────────────────

  /** §2.5 — an elapsed-seconds tick, alive ONLY while a generate is in flight (NFR-5). */
  useEffect(() => {
    if (step !== 'generate') return;
    const started = Date.now();
    setElapsedS(0);
    const handle = window.setInterval(() => {
      setElapsedS(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => {
      window.clearInterval(handle);
    };
  }, [step]);

  const generate = useCallback((): void => {
    const current = latest.current;
    if (current === null) return;
    /**
     * B-30 / §20 — **a full card refuses the generate before it is issued.**
     *
     * The commit seam below already slices by `room` and no-ops at `room <= 0` (R-6 declined,
     * twice: the twenty counts rejected entries too, and that is the ruled behaviour). What that
     * left was a model call whose entire answer was discarded on arrival — paid for, waited
     * through at the break, and then silently dropped. So the check moves ahead of the request:
     * nothing is spent, and the surface says why and what to do about it.
     *
     * The step does not advance. A refusal that pushed the user into the 'generate' screen would
     * be a spinner for a request that was never made.
     */
    if (config.COACH_CARD_MAX_ENTRIES - current.adjustments.length <= 0) {
      setNotice(CARD_FULL_NOTE);
      log.info(MODULE, 'a generate was declined: the card is already full', {
        entries: current.adjustments.length,
      });
      return;
    }
    setFailure(null);
    setNotice(null);
    setStep('generate');

    const controller = new AbortController();
    generating.current = controller;
    const request = buildCoachRequestB({
      match,
      context,
      entries,
      selectedHypothesisIds: current.selectedHypothesisIds,
      roster,
      shape,
    });

    void generateSuggestions(request, { signal: controller.signal }).then(
      (outcome) => {
        generating.current = null;
        if (!alive.current) return;
        if (outcome.kind === 'cancelled') {
          // §2.5's boundary behavior: the whistle (or an unmount) took the attempt. Nothing
          // commits and nothing renders — no error may reach the capture surface.
          return;
        }
        if (outcome.kind === 'failed') {
          setFailure(outcome.code);
          setStep('review');
          return;
        }
        // §2.5 — all suggestions commit in ONE transaction, then step 3. The slice is §4.2's
        // entries cap: a response into an almost-full draft must not cost the whole write
        // (`ERR_CARD_TOO_LARGE` rejects the row, not the overflow).
        const at = Date.now();
        enqueue(
          'the generated suggestions',
          (row) => {
            const room = config.COACH_CARD_MAX_ENTRIES - row.adjustments.length;
            if (room <= 0) return { ...row, generatedAt: at };
            return {
              ...row,
              adjustments: [
                ...row.adjustments,
                ...outcome.suggestions
                  .slice(0, room)
                  .map((suggestion) => aiEntryB(suggestion, newId(), at)),
              ],
              generatedAt: at,
            };
          },
          () => {
            setStep('review');
            /**
             * B-30 / §20 — say it once, here, where the answer it describes is on screen.
             *
             * After `enqueue`'s own `setNotice(null)`, deliberately: the commit's success clearing
             * a stale engine message must not also clear the report about the commit that just
             * happened. `null` on every other answer, which is why nothing is dismissed on the
             * ordinary path.
             */
            const trimmed = truncationNoticeB(
              outcome.returned,
              outcome.suggestions.length,
              config.COACH_MAX_SUGGESTIONS,
            );
            if (trimmed !== null) setNotice(trimmed);
          },
        );
      },
      (cause: unknown) => {
        generating.current = null;
        if (!alive.current) return;
        // The facade returns its failures as values; a throw here is a defect, not a taxonomy
        // arm — reported, and the user still lands in manual entry with a card to write.
        const message = cause instanceof Error ? cause.message : String(cause);
        log.error(MODULE, 'the generate threw rather than returning an outcome', { message });
        setFailure('serverError');
        setStep('review');
      },
    );
  }, [match, context, entries, roster, shape, enqueue]);

  /** §2.5 — "Skip to writing", visible throughout the generate. Aborts, never errors. */
  const skipToWriting = useCallback((): void => {
    generating.current?.abort();
    generating.current = null;
    setStep('review');
  }, []);

  /**
   * §2.5's boundary behavior — cancel any in-flight generate on a `Match.state` change, and on
   * unmount. The first run is skipped: mounting is not a state change, and aborting the
   * controller that has not been created yet would be a no-op with a confusing footprint.
   */
  const seenState = useRef(match.state);
  useEffect(() => {
    if (seenState.current === match.state) return;
    seenState.current = match.state;
    generating.current?.abort();
    generating.current = null;
  }, [match.state]);

  useEffect(
    () => () => {
      generating.current?.abort();
      generating.current = null;
    },
    [],
  );

  // ── Finalize / discard ─────────────────────────────────────────────────────

  const finalize = useCallback((): void => {
    const current = latest.current;
    if (current === null) return;
    setBusy(true);
    chain.current = chain.current
      .then(async () => {
        const row = latest.current;
        if (row === null) return;
        const result = await finalizeCard(match.id, row.id);
        if (!alive.current) return;
        if (!result.ok) {
          setNotice(result.message);
          await reread();
          return;
        }
        await reread();
        if (!alive.current) return;
        setStep('done');
      })
      .catch((cause: unknown) => {
        const message = cause instanceof Error ? cause.message : String(cause);
        setNotice(message);
        log.error(MODULE, 'the card could not be finalized', { message });
      })
      .finally(() => {
        if (alive.current) setBusy(false);
      });
  }, [match.id, reread]);

  const discard = useCallback(
    (onDiscarded: () => void): void => {
      const current = latest.current;
      if (current === null || current.status === 'final') return;
      setBusy(true);
      chain.current = chain.current
        .then(async () => {
          const row = latest.current;
          if (row === null) return;
          const result = await discardDraft(match.id, row.id);
          if (!alive.current) return;
          if (!result.ok) {
            setNotice(result.message);
            return;
          }
          latest.current = null;
          onDiscarded();
        })
        .catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'the draft could not be discarded', { message });
        })
        .finally(() => {
          if (alive.current) setBusy(false);
        });
    },
    [match.id],
  );

  return {
    state: { phase, step, entries, roster, shape, card, halftimeCard, notice, failure, elapsedS, busy },
    actions: {
      toggleSelection,
      selectAll,
      setEntryStatus,
      saveEdit,
      addManual,
      removeEntry,
      generate,
      skipToWriting,
      finalize,
      discard,
      goToSelect: () => {
        setStep('select');
      },
      goToReview: () => {
        setStep('review');
      },
      /** B-15 — an annotation written from the Step-1 detail sheet; the snippets re-read. */
      refresh: () => {
        reread().catch((cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'the workflow could not be re-read', { message });
        });
      },
      dismissNotice: () => {
        setNotice(null);
      },
    },
  };
}

export type WorkflowActionsB = ReturnType<typeof useWorkflowB>['actions'];
